import { useState } from 'react';
import cn from 'classnames';
import { Day } from '../Day/Day';
import {
  GET_FIRST_DAY_OF_MONTH,
  GET_NUMBER_DAYS_IN_MONTH,
  MAX_SLOTS_IN_MONTH,
} from '../../utils/utils';

import styles from './DaySelector.module.scss';
import { DatePickerDayStyle } from '../../DatePicker';

interface DaySelectorProps {
  YYYY: string;
  MM: string;
  DD: string;
  daysNames?: [string, string, string, string, string, string, string];
  onClick?: (event: any, day: string) => void;
  selectedYYYY?: string;
  selectedMM?: string;
  daysStyle?: DatePickerDayStyle[];
  minDate?: Date;
  maxDate?: Date;
}

const DAY_NAMES_DEFAULT: DaySelectorProps['daysNames'] = [
  'Sun',
  'Mon',
  'Tue',
  'Wed',
  'Thu',
  'Fri',
  'Sat',
];

export const DaySelector = ({
  YYYY,
  MM,
  DD,
  daysNames = DAY_NAMES_DEFAULT,
  onClick = (event: any, day: string) => {},
  selectedMM,
  selectedYYYY,
  minDate,
  maxDate,
  daysStyle = new Array(31).fill('normal'),
}: DaySelectorProps) => {
  const [days] = useState<number[]>(Array(MAX_SLOTS_IN_MONTH).fill(null));

  const renderDays = () => {
    const numberDays = GET_NUMBER_DAYS_IN_MONTH(Number(YYYY), Number(MM) - 1);
    const firstDayOfMonth = GET_FIRST_DAY_OF_MONTH(
      Number(YYYY),
      Number(MM) - 1
    );
    const lastDayVisibleOfLastMonth =
      GET_NUMBER_DAYS_IN_MONTH(
        Number(MM) - 1 === -1 ? Number(YYYY) - 1 : Number(YYYY),
        Number(MM) - 1 === -1 ? 12 : Number(MM) - 1
      ) - firstDayOfMonth;

    return days?.map((day, index) => {
      if (index < firstDayOfMonth) {
        const d = index + lastDayVisibleOfLastMonth + 1;

        return (
          <Day
            className={cn(styles.out)}
            key={Math.random()}
            day={d}
            ariaLabel={`${MM}-${day}-${YYYY}`}
            onClick={() => {}}
            isDisabled
          />
        );
      }

      if (index >= numberDays + firstDayOfMonth) {
        const d = index - numberDays - firstDayOfMonth + 1;

        return (
          <Day
            className={cn(styles.out)}
            key={Math.random()}
            day={d}
            ariaLabel={`${MM}-${day}-${YYYY}`}
            onClick={() => {}}
            isDisabled
          />
        );
      }

      const d =
        index >= firstDayOfMonth && index < numberDays + firstDayOfMonth
          ? index - firstDayOfMonth + 1
          : null;

      const getIsDisabled = () => {
        const actualDate = new Date(
          `${MM}-${d.toString().padStart(2, '0')}-${YYYY}`
        ).getTime();

        return !(
          actualDate >= minDate?.getTime() && actualDate <= maxDate?.getTime()
        );
      };

      const getIsSelected = () => {
        return (
          Number(DD) === d &&
          DD.length === 2 &&
          selectedMM === MM &&
          selectedYYYY === YYYY
        );
      };

      const s = daysStyle?.[d - 1];

      return (
        <Day
          className={cn(
            styles.out,
            !getIsDisabled() && !getIsSelected() && styles[s],
            getIsSelected() && styles['selected-day']
          )}
          key={Math.random()}
          day={d}
          ariaLabel={`${MM}-${day}-${YYYY}`}
          isDisabled={getIsDisabled() || s === 'disabled'}
          onClick={(event: any) => {
            onClick(event, String(d).padStart(2, '0'));
          }}
        />
      );
    });
  };

  const renderNameDays = () =>
    daysNames.map((name) => {
      return (
        <li className={styles['day-name']} key={name}>
          {name}
        </li>
      );
    });

  return (
    <ul className={styles.root}>
      {renderNameDays()}
      {renderDays()}
    </ul>
  );
};
